/* eslint-disable arrow-parens */
/* eslint-disable semi */
/* eslint-disable react/function-component-definition */
import React, { useState, useEffect, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { ApplicationFetch } from '../../../controllers/ApplicationFetch';
import { HttpError } from '../../../shared/FetchBase';
import { TextField } from '../../../shared/fields/TextField';
import { Checkbox } from '../../../shared/fields/Checkbox';

interface MatchParams {
    applicationID: string;
}

interface AssignAgentProps extends RouteComponentProps<MatchParams> { }

interface AssignAgentDTO {
    applicationID: string;
    claimRandomIdentifier: string;
    agentCode: string;
}

const C_DECLARATION_MSG = `I declare and acknowledge that by entering this grant agent code I authorise the owner of this grant agent code to access my EMDG application, grant agreement and milestone reports and act on my behalf in correspondence with Austrade. 
Additionally, I authorise the owner of this grant agent code to be included in any correspondence from Austrade on matters relating to my EMDG grant application, grant agreement and milestone report.
`
const C_AGENT_NOT_FOUND_MSG = 'The Agent Code you provided could not be found. Please try again or contact EMDG for help at <a href="mailto:emdg.help@austrade.gov.au">emdg.help@austrade.gov.au</a>.'
const C_AGENT_UPDATED_SUCCESS_MSG = 'Your application has been updated with your agent\'s details.'

const AssignAgentToApplication: React.FC<AssignAgentProps> = ({ match, history }) => {
  const { applicationID } = match.params;

  const [claimRandomIdentifier, setClaimRandomIdentifier] = useState<string>('');
  const [agentID, setAgentID] = useState<string>('');
  const [declarationChecked, setDeclarationChecked] = useState<boolean>(false);
  const [alert, setAlert] = useState<{ message: string | null, type: 'success' | 'danger' | null }>({ message: null, type: null });
  const [previousLinkFound, setPreviousLinkFound] = useState<boolean>(false);

  const agentIDRef = useRef(null);

  const applicationFetch = new ApplicationFetch();

  useEffect(() => {
    // get ClaimRandomIdentifier by applicationID, and AgentCode if found;
    applicationFetch.getEmdgApplicationNumberById(applicationID)
      .then(data => {
        setClaimRandomIdentifier(data.claimRandomIdentifier);
        if (data.agentCode) {
          setAgentID(data.agentCode);
          setPreviousLinkFound(true);
        }
      })
      .catch(error => {
        console.error('Error fetching ClaimRandomIdentifier:', error);
        setAlert({ message: 'Error fetching Application-ID', type: 'danger' });
      });
  }, [applicationID]);

  const handleAgentIDChange = (value: string) => {
    setAgentID(value);
    setAlert({ message: null, type: null });
  };

  const validateField = (fieldRef: React.RefObject<any>): Promise<boolean> => new Promise((resolve) => {
    if (fieldRef.current) {
      fieldRef.current.validate((isValid: boolean) => {
        resolve(isValid);
      }, true);
    } else {
      resolve(false);
    }
  });

  const handleSubmit = async () => {
    const isValidAgentID = await validateField(agentIDRef);

    if (!isValidAgentID) { return; }

    // continue form submission...
    const data: AssignAgentDTO = {
      applicationID,
      claimRandomIdentifier,
      agentCode: agentID,
    };

    // validate agentCode and insert, or fail
    applicationFetch.assignAgentToApplication(data)
      .then(() => {
        setAlert({ message: C_AGENT_UPDATED_SUCCESS_MSG, type: 'success' });
        setPreviousLinkFound(true); // Set previousLinkFound to true on success, so as to make form readonly.
      })
      .catch(error => {
        let message = 'Unexpected error occurred.';
        const type: ('success' | 'danger' | null) = 'danger';

        if (error instanceof HttpError) {
          if (error.status === 404) {
            message = C_AGENT_NOT_FOUND_MSG;
          } else {
            message = error.message || message;
          }
          console.error('HTTP Error:', error.message, 'Status:', error.status);
        } else {
          message = error.message || message;
          console.error(`Error submitting Agent Code: ${data.agentCode}`, error);
        }

        setAlert({ message, type });
      });
  };

  return (
    <div className="container-fluid pt-2">
      <div className="row">
        <div className="col-lg-9">
          <h3>Add agent details</h3>

          <div className="form-item">
            <TextField
              ref={agentIDRef}
              id="agentID"
              label={`Please provide the Agent Code of the agent who assisted you in preparing application ${claimRandomIdentifier}.`}
              value={agentID}
              onChange={handleAgentIDChange}
              regex="^[A-Za-z]{3}[0-9]{2}$"
              regexMessage="Agent Code format is incorrect"
              required
              requiredMessage="This field is required"
              maxLength={5}
              block={false}
              disabled={previousLinkFound}
            />
          </div>

          <div className="form-item">
            <Checkbox
              id="chkDeclaration"
              required
              requiredMessage="You must accept terms and conditions"
              onChange={(val) => setDeclarationChecked(val)}
              value={declarationChecked}
              label={C_DECLARATION_MSG}
              disabled={previousLinkFound}
            />
          </div>

          {alert.message ? (
            <Alert
              variant={alert.type}
              className={`emdg-page-alerts ${alert.type === 'success' ? 'emdg-page-alerts--success' : 'emdg-page-alerts--error'}`}
            >
              <div dangerouslySetInnerHTML={{ __html: alert.message }} />
            </Alert>
          ) : (
            <div style={{ height: '62px' }} />
          )}

          <div className="row">
            <div className="col-md-3">
              <Button
                className="emdg-btn emdg-btn--secondary"
                onClick={() => history.goBack()}
              >
                Back
              </Button>
            </div>
            <div className="col-md-9">
              <div className="float-right">
                <Button
                  className="emdg-btn ml-2"
                  onClick={handleSubmit}
                  disabled={!declarationChecked || previousLinkFound}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignAgentToApplication;
